import { useState } from 'react'

import { Box, Link, Text } from '@factorialco/deprecated-design-system'
import { Icon } from '@factorialco/factorial-one-react'
import { Lightbulb } from '@factorialco/factorial-one-react/icons/app'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { Field } from 'factorial-form'
import { GenerateAutoScheduleDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import DropdownSearchBar from 'modules/core/components/DropdownSearchBar'
import useEmployeeMultiFilters, {
  applyFilters,
} from 'modules/core/components/EmployeeMultiFilters/hooks/useEmployeeMultiFilters'
import EmployeeSelectorInput from 'modules/core/components/Inputs/EmployeeSelector'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import getFilterValue from 'modules/shift_management/components/ShiftManagement/Content/_filters/getFilterValue'
import { SHIFT_MANAGEMENT_FILTERS } from 'modules/shift_management/hooks/reducers/filter'
import useLocationOptions from 'modules/shift_management/hooks/useLocationOptions'
import { useOnSelectEmployees } from 'modules/shift_management/hooks/useOnSelectEmployees'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectableEmployees } from 'modules/shift_management/hooks/useSelectableEmployees'
import { AutoScheduleForm } from 'modules/shift_management/lib/forms/AutoScheduleForm'

import { Footer } from './Footer'
import { Rules } from './Rules'

interface Props {
  onClose: () => void
  contractVersions: ContractVersions
  setIsSubscribed: (isSubscribed: boolean) => void
}

const AutoScheduleModal = observer(({ onClose, contractVersions, setIsSubscribed }: Props) => {
  const [selectedEmployees, setSelectedEmployees] = useState<number[]>([])

  const [{ view }] = useSchedule()
  const form = useLazyRef(
    () =>
      new AutoScheduleForm({
        start_at: view.startAt,
        end_at: view.endAt,
      })
  ).current
  const { selectableEmployees, loading: loadingEmployees } = useSelectableEmployees(
    view.startAt,
    view.endAt,
    contractVersions
  )
  const { onSelectEmployee } = useOnSelectEmployees({
    form,
    selectedEmployees,
    setSelectedEmployees,
  })

  const { filters, logicOperator } = useEmployeeMultiFilters(
    getFilterValue,
    LOCAL_STORAGE_KEYS.shiftEmployees
  )

  const locationFilter = filters?.find(
    (f) => f.values.field === SHIFT_MANAGEMENT_FILTERS.employeeLocation
  )

  const locationField = new Field(locationFilter?.values?.value || '', 'string')

  const { locationOptions } = useLocationOptions({
    locationField,
  })

  const defaultLocation = locationOptions.find(
    (option) => option.value.toString() === locationField.value.toString()
  )

  useUnsafeEffect(() => {
    const setEmployees = async () => {
      const value = await applyFilters(
        selectableEmployees,
        filters,
        logicOperator,
        getFilterValue,
        () => undefined,
        contractVersions
      )
      setSelectedEmployees(() => value.map((user) => user.id))
    }
    setEmployees()
  }, [selectableEmployees])

  useUnsafeEffect(() => {
    const locationFormValue = form.get('location_id')
    locationFormValue.set(Number(locationField.value))
  }, [locationField])

  useUnsafeEffect(() => {
    form.setValues({ employee_id: selectedEmployees })
  }, [selectedEmployees])

  const [createAutoSchedule, { loading }] = useMutation(GenerateAutoScheduleDocument, {
    extractorFn: (data) => data.shiftManagementMutations.generateScheduleShiftmanagementAutomation,
  })

  const onSubmit = async (closePortal: ClosePortalFn) => {
    const res = await createAutoSchedule({
      variables: {
        agreementHours: form.get('agreement_hours').value,
        employeesIds: form.get('employee_id').value,
        endAt: form.get('end_at').value,
        locationId: Number(form.get('location_id').value),
        maxWorkHoursPerDay: form.get('max_work_hours_per_day').value,
        restDaysPerWeek: form.get('rest_days_per_week').value,
        restHoursBetweenShifts: form.get('rest_hours_between_shifts').value,
        startAt: form.get('start_at').value,
      },
    })
    const handled = handleResult(res, (_errors) => {})
    if (handled.success) {
      setIsSubscribed(true)
      closePortal()
    }
  }

  return (
    <Modal
      illustration="window-star"
      title={i18n.t('shift_management.coverage_section.automation.modal.title')}
      description={i18n.t('shift_management.coverage_section.automation.modal.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={<Footer onClose={onClose} disabled={loading || selectedEmployees.length === 0} />}
      disableFocusTrap
    >
      <Box width="full" flexDirection="column" gap="s16">
        <DropdownSearchBar
          searchLabel={i18n.t('common.search')}
          sortLabel={i18n.t('common.name')}
          selectLabel={i18n.t('common.select')}
          labelNotFound={i18n.t('common.select')}
          label={i18n.t('shift_management.add_shift.location')}
          data={[]}
          name={defaultLocation?.label || i18n.t('common.select')}
          onSelect={() => {}}
          autofocus={false}
          disabled
        />

        <Rules form={form} />

        <Box background="viridian100" padding="s24" borderRadius={{ all: 'abs012' }}>
          <Box flexDirection="row" gap="s4">
            <Icon icon={Lightbulb} size="md" />
            <Text.Header3>
              {i18n.t('shift_management.coverage_section.automation.modal.info.title')}
            </Text.Header3>
          </Box>
          <Text.Normal>
            {i18n.t('shift_management.coverage_section.automation.modal.info.description')}
            <Link.Href href="" target="_blank">
              {i18n.t('common.learn_more')}
            </Link.Href>
          </Text.Normal>
        </Box>

        {loadingEmployees ? (
          <Loading />
        ) : (
          <EmployeeSelectorInput
            hasSubmitButton
            isMultiSelect
            attachment="top center"
            field={form.get('employee_id')}
            label={i18n.t('common.employees')}
            employees={selectableEmployees}
            onSelectEmployee={onSelectEmployee}
            selectedEmployeeIds={selectedEmployees}
            virtualized
            contractVersions={contractVersions}
            filterProps={{
              defaultOption: {
                label: defaultLocation?.label || '',
                value: locationField.value,
              },
            }}
          />
        )}
      </Box>
    </Modal>
  )
})

export { AutoScheduleModal }
